import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronDown, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INQUIRY_OPTIONS = [
  { value: "AI", label: "AI Solutions" },
  { value: "Staffing", label: "Staffing / RecruitSteps" },
  { value: "Both", label: "Both — AI + Staffing" },
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    inquiry_type: "AI",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim() || !form.email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, form);
      setSuccess(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", inquiry_type: "AI", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't submit your enquiry. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="relative py-24 md:py-32"
    >
      <div className="pointer-events-none absolute inset-x-0 top-1/3 mx-auto w-[640px] h-[640px] rounded-full bg-indigo-600/15 blur-[160px]" />

      <div className="relative max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-400">
            Start the conversation
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tighter text-white">
            Tell us what you&rsquo;re building.
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Whether it&rsquo;s a workflow, a voice agent, or your next hire — we
            reply within one business day.
          </p>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass rounded-2xl p-7 md:p-10 relative overflow-hidden"
        >
          {success ? (
            <div
              data-testid="contact-success"
              className="text-center py-10 flex flex-col items-center gap-4"
            >
              <CheckCircle2 className="w-12 h-12 text-indigo-400" />
              <h3 className="font-display text-2xl text-white">
                Message received.
              </h3>
              <p className="text-slate-300 max-w-md">
                Thanks for reaching out — a member of our team will be in touch
                within one business day.
              </p>
              <button
                data-testid="contact-submit-another"
                type="button"
                onClick={() => setSuccess(false)}
                className="mt-3 px-5 py-2.5 rounded-full glass hover:border-white/25 text-sm text-white transition-all"
              >
                Send another
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
                  Full name
                </label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
                  Work email
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@company.com"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
                  Inquiry type
                </label>
                <div className="relative">
                  <select
                    data-testid="contact-select-type"
                    value={form.inquiry_type}
                    onChange={update("inquiry_type")}
                    className="appearance-none w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3.5 pr-12 text-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none transition-all"
                  >
                    {INQUIRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} className="bg-[#0B1120] text-white">
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
                  Tell us a bit more <span className="text-slate-600">(optional)</span>
                </label>
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="What problem are you trying to solve?"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 outline-none transition-all resize-none"
                />
              </div>

              {error && (
                <div
                  data-testid="contact-error"
                  className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="mt-1 inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium transition-all duration-300 shadow-[0_0_28px_rgba(99,102,241,0.45)] hover:shadow-[0_0_44px_rgba(99,102,241,0.7)]"
              >
                {submitting ? "Sending..." : "Send Inquiry"}
                {!submitting && <Send className="w-4 h-4" />}
              </button>

              <p className="text-xs text-slate-500 text-center">
                By submitting, you agree to be contacted by our team about your
                inquiry.
              </p>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}
